import { html, nothing, type PropertyValues } from 'lit';
import { Video } from '@vitesse/proto/vitesse/v1/video_pb.js';
import { customElement, property, state } from 'lit/decorators.js';
import { BasePage } from '@vitesse/base-ui/components/base-page';
import { t } from '@vitesse/base-ui/i18n';
import type { CameraManager } from '../managers/camera-manager';
import type { CameraStreamManager } from '../components/camera/camera-stream-manager';
import type { Camera } from '../../core/domain/camera';
import { type PillTab } from '@vitesse/base-ui/components/templates';
import { button } from '@vitesse/base-ui/components/base-buttons';
import { cameraStatusBadges, type CameraFeedStats } from '../components/camera/camera-badges';
import '../components/camera/camera-feed';
import '../components/camera/camera-recordings';
import '../components/properties-view';

/*
 * <vitesse-camera-detail> — the single-camera page. Extends BasePage for the shared
 * back·title·badges chrome, no title icon (a camera page is obviously a camera).
 * Pill tabs: Live (the full-bleed feed), Recording (recorded-buffer playback +
 * download), and Properties (the camera's backend-authored Info view).
 *
 * The Live feed stays MOUNTED across all tabs (hidden, not destroyed) — so coming
 * back to Live shows the frozen last frame INSTANTLY and resumes fast (the shared
 * PC + GOP cache), instead of a black re-handshake from scratch.
 */

type CameraTab = 'live' | 'recording' | 'properties';

@customElement('vitesse-camera-detail')
export class CameraDetail extends BasePage {
  @property({ attribute: false }) cameraManager?: CameraManager;
  @property({ attribute: false }) cameraStreams?: CameraStreamManager;
  @property() cameraId = '';
  @property() gatewayId = '';
  @property() gatewayName = '';

  @state() private camera?: Camera;
  @state() private tab: CameraTab = 'live';
  @state() private stats?: CameraFeedStats;

  private readonly onFeedStats = (e: Event): void => {
    this.stats = (e as CustomEvent<CameraFeedStats>).detail;
  };

  private readonly onChange = (): void => {
    const next = this.cameraManager?.get(this.cameraId);
    // The camera vanished from the roster while we're viewing it (unplugged / feed
    // lost) → pop back to the gateway detail rather than sit on a dead page. Guarded
    // on `this.camera` so the initial pre-roster render (never had it) doesn't bounce.
    if (this.camera && !next) {
      this.dispatchEvent(new CustomEvent('navigate', { detail: { view: 'gateway' }, bubbles: true, composed: true }));
      return;
    }
    this.camera = next;
  };

  connectedCallback(): void {
    super.connectedCallback();
    this.cameraManager?.addEventListener('change', this.onChange);
    this.addEventListener('feedstats', this.onFeedStats); // from the Live feed → header badges
    this.onChange();
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.cameraManager?.removeEventListener('change', this.onChange);
    this.removeEventListener('feedstats', this.onFeedStats);
  }

  // Cold reload (the per-gateway manager arrives a tick after the route) + camera→
  // camera nav (Lit reuses this element): re-bind the roster listener + re-read.
  protected willUpdate(changed: PropertyValues): void {
    if (changed.has('cameraManager')) {
      (changed.get('cameraManager') as CameraManager | undefined)?.removeEventListener('change', this.onChange);
      this.cameraManager?.addEventListener('change', this.onChange);
      this.onChange();
    }
    if (changed.has('cameraId')) this.onChange();
  }

  // ── BasePage hooks ─────────────────────────────────────────────────────────
  protected pageTitle(): string {
    return this.camera?.name || this.cameraId;
  }
  protected titleIcon(): string | null {
    return null; // no SVG in the title — a camera page is self-evidently a camera
  }
  protected backLabel(): string {
    return this.gatewayName || t('camera.back');
  }
  protected onBack(): void {
    this.dispatchEvent(new CustomEvent('navigate', { detail: { view: 'gateway' }, bubbles: true, composed: true }));
  }
  protected renderBadges() {
    return html`${cameraStatusBadges(this.camera, this.stats)}`;
  }
  // Header action row (left → right): Download (only with footage) · Pause/Resume
  // (only while recording is enabled) · the enable/disable recording toggle. Each
  // is a base button; states flip via the roster subscription after the RPC.
  protected renderActions() {
    const c = this.camera;
    if (!c) return nothing;
    const rec = c.recordingEnabled;
    const hasFootage = c.availableFromMs > 0;
    return html`
      ${hasFootage
        ? button({
            label: t('camera.downloadRecording'),
            icon: 'download',
            color: 'blue',
            onClick: () => void this.downloadRecording(),
          })
        : nothing}
      <vitesse-gated .rpc=${Video.method.setRecordingPaused} inline>
        ${rec
          ? button({
              label: c.recordingPaused ? t('camera.resumeRecording') : t('camera.pauseRecording'),
              icon: c.recordingPaused ? 'play' : 'pause',
              color: c.recordingPaused ? 'green' : 'yellow',
              onClick: () => void this.togglePause(),
            })
          : nothing}
      </vitesse-gated>
      <vitesse-gated .rpc=${Video.method.setRecording} inline>
        ${button({
          label: rec ? t('camera.disableRecording') : t('camera.enableRecording'),
          icon: rec ? 'cctv-off' : 'cctv',
          color: rec ? 'red' : 'blue',
          onClick: () => void this.toggleRecording(),
        })}
      </vitesse-gated>
    `;
  }
  protected aiHints(): string[] {
    if (this.tab === 'recording') return [t('camera.hintRecordingGaps'), t('camera.hintRecordingMotion')];
    if (this.tab === 'properties') return [t('camera.hintPropsConfig')];
    return [t('camera.hintLiveShowing'), t('camera.hintLiveDropped')];
  }

  private async toggleRecording(): Promise<void> {
    const c = this.camera;
    if (!c || !this.cameraManager) return;
    try {
      await this.cameraManager.setRecording(c.id, !c.recordingEnabled);
    } catch (err) {
      console.error('[camera] setRecording failed', err);
    }
  }

  private async togglePause(): Promise<void> {
    const c = this.camera;
    if (!c || !this.cameraManager) return;
    try {
      await this.cameraManager.setRecordingPaused(c.id, !c.recordingPaused);
    } catch (err) {
      console.error('[camera] setRecordingPaused failed', err);
    }
  }

  // Server-export the recorded span → one MP4. The session Bearer + gateway-id ride the
  // fetch HEADERS (an anchor/href download can't carry them, and the token must NOT go
  // in the URL). On Chromium we STREAM the response straight to disk via the File
  // System Access API — the browser writes each chunk as it arrives, so the download
  // starts the instant the first byte lands (was: buffer the ENTIRE multi-GB export
  // into a RAM blob before anything happened — minutes of nothing, OOM risk on 20 GB).
  private async downloadRecording(): Promise<void> {
    const c = this.camera;
    if (!c || !this.cameraManager) return;
    const fname = `${c.name || c.id}.mp4`;
    const headers: Record<string, string> = { 'X-Vitesse-Gateway-Id': this.cameraManager.gateway };
    try {
      const url = await this.cameraManager.exportUrl(c.id);
      const token = await this.cameraManager.getToken();
      if (token) headers.Authorization = `Bearer ${token}`;

      // Preferred: stream to disk (no buffering). Chromium-only — falls back below.
      const picker = (window as unknown as { showSaveFilePicker?: (o: unknown) => Promise<FileSystemFileHandle> })
        .showSaveFilePicker;
      if (typeof picker === 'function') {
        let handle: FileSystemFileHandle;
        try {
          handle = await picker({ suggestedName: fname, types: [{ accept: { 'video/mp4': ['.mp4'] } }] });
        } catch (e) {
          if ((e as Error)?.name === 'AbortError') return; // user dismissed the save dialog
          throw e;
        }
        const writable = await handle.createWritable();
        const res = await fetch(url, { headers });
        if (!res.ok || !res.body) {
          await writable.close().catch(() => undefined);
          throw new Error(`export ${res.status}`);
        }
        await res.body.pipeTo(writable); // streams to disk + closes the file on completion
        return;
      }

      // Fallback (non-Chromium): buffer then anchor-download (the old path).
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`export ${res.status}`);
      const blobUrl = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      console.error('[camera] download failed', err);
    }
  }

  protected tabs(): PillTab[] {
    return [
      { id: 'live', label: t('camera.tabLive') },
      { id: 'recording', label: t('camera.tabRecording') },
      { id: 'properties', label: t('camera.tabProperties') },
    ];
  }
  protected activeTabId(): string {
    return this.tab;
  }
  protected onTabChange(id: string): void {
    if (id !== this.tab) this.tab = id as CameraTab;
  }

  protected renderContent() {
    return html`${CAM_DETAIL_STYLE}
      <!-- Live feed: ALWAYS mounted (hidden off-tab) so returning is instant (frozen
           frame + fast resume), never a black re-handshake. -->
      <div class="cam-stage" ?hidden=${this.tab !== 'live'}>
        <vitesse-camera-feed
          variant="detail"
          .gatewayId=${this.gatewayId}
          .cameraId=${this.cameraId}
          .cameraStreams=${this.cameraStreams}
        ></vitesse-camera-feed>
        ${this.camera?.status === 'error' && this.camera.errorDetail
          ? html`<p class="cam-err">${this.camera.errorDetail}</p>`
          : nothing}
      </div>
      ${this.tab === 'recording'
        ? html`<vitesse-camera-recordings
            .cameraManager=${this.cameraManager}
            .cameraStreams=${this.cameraStreams}
            .camera=${this.camera}
            .gatewayId=${this.gatewayId}
          ></vitesse-camera-recordings>`
        : nothing}
      ${this.tab === 'properties'
        ? html`<vitesse-properties-view
            .view=${this.camera?.properties ?? { tables: [] }}
          ></vitesse-properties-view>`
        : nothing}`;
  }
}

const CAM_DETAIL_STYLE = html`<style>
  /* Live: fill the whole content area (BasePage's .base-page-content is flex:1).
     The [hidden] override beats the .cam-stage display:flex so off-tab truly hides
     (UA [hidden]{display:none} loses to a class selector otherwise). */
  .cam-stage {
    height: 100%;
    min-height: 60vh;
    display: flex;
    flex-direction: column;
  }
  .cam-stage[hidden] {
    display: none;
  }
  .cam-stage vitesse-camera-feed {
    flex: 1;
    min-height: 0;
  }
  .cam-err {
    margin: 8px 2px 0;
    color: var(--red, #ef4444);
    font-size: 13px;
  }
</style>`;

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-camera-detail': CameraDetail;
  }
}
